import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { CommonModule } from '@angular/common';

import { Component, effect, inject, signal, untracked } from '@angular/core';

import { FormsModule } from '@angular/forms';

import { ByeLawNorm, HierarchyType, SbcApiService } from '../services/sbc-api.service';

import { SessionService } from '../../../core/services/session.service';



type NormTab = 'PLOT_SIZE' | 'FAR' | 'HEIGHT' | 'SETBACK' | 'ELECTRIC';



const NORM_TYPE_FILTERS = [

  { value: '', label: 'All norm types' },

  { value: 'PLOT_SIZE', label: 'Plot size' },

  { value: 'FAR', label: 'FAR / coverage' },

  { value: 'HEIGHT', label: 'Height' },

  { value: 'SETBACK', label: 'Setback' },

  { value: 'ELECTRIC', label: 'Electric line' },

] as const;



const NORM_TABS: { id: NormTab; label: string; clause: string }[] = [

  { id: 'PLOT_SIZE', label: 'Plot size 3.2.1', clause: '3.2.1' },

  { id: 'FAR', label: 'FAR / coverage 3.2.2', clause: '3.2.2' },

  { id: 'HEIGHT', label: 'Height 3.2.3', clause: '3.2.3' },

  { id: 'SETBACK', label: 'Setback 3.2.4', clause: '3.2.4.1' },

  { id: 'ELECTRIC', label: 'Electric line 3.2.5', clause: '3.2.5' },

];



@Component({

  selector: 'app-bye-laws',

  standalone: true,

  imports: [CommonModule, PageBackButtonComponent, FormsModule],

  templateUrl: './bye-laws.component.html',

  styleUrl: './bye-laws.component.css',

})

export class ByeLawsComponent {

  private readonly api = inject(SbcApiService);

  readonly session = inject(SessionService);



  readonly normTypeFilters = NORM_TYPE_FILTERS;

  readonly normTabs = NORM_TABS;



  rows = signal<ByeLawNorm[]>([]);

  subtypes = signal<HierarchyType[]>([]);

  categories = signal<HierarchyType[]>([]);

  error = signal('');

  toast = signal('');

  loading = signal(false);

  modalOpen = signal(false);

  editingId = signal<number | null>(null);

  saving = signal(false);

  page = signal(1);
  readonly pageSize = signal(10);
  total = signal(0);
  totalPages = signal(1);

  search = '';

  categoryFilter = '';

  normTypeFilter = '';



  form = this.emptyForm();



  constructor() {
    effect(() => {
      this.session.authorityId();
      untracked(() => {
        this.page.set(1);
        this.loadHierarchy();
        this.reload();
      });
    });
  }

  filteredSubtypesForForm(): HierarchyType[] {
    const catId = Number(this.form.categoryId);
    if (!catId) return [];
    return this.subtypes().filter((s) => s.parentId === catId);
  }



  emptyForm() {

    return {

      normTab: 'PLOT_SIZE' as NormTab,

      categoryId: '',

      useTypeId: '',

      clauseRef: '3.2.1',

      effectiveFrom: '2025-01-01',

      effectiveTo: '',

      plotAreaMin: '',

      plotAreaMax: '',

      roadWidthMin: '',

      roadWidthMax: '',

      groundCoveragePct: '',

      farBase: '',

      farMax: '',

      heightMax: '',

      setbackFront: '',

      setbackRear: '',

      setbackSide1: '',

      setbackSide2: '',

      attributesJson: '',

      remarks: '',

    };

  }



  loadHierarchy(): void {
    this.api.listHierarchyTypes({ module: 'BYELAW_NORMS', level: 'subtype', pageSize: '200' }).subscribe({
      next: (res) => this.subtypes.set(res.data || []),
    });
    this.api.listHierarchyTypes({ module: 'BYELAW_NORMS', level: 'category', pageSize: '100' }).subscribe({
      next: (res) => this.categories.set(res.data || []),
    });
  }

  onFilterChange(): void {
    this.page.set(1);
    this.reload();
  }

  onSearchChange(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.onFilterChange(), 320);
  }

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  reload(): void {
    this.loading.set(true);
    this.error.set('');
    const params: Record<string, string> = {
      page: String(this.page()),
      pageSize: String(this.pageSize()),
    };
    if (this.categoryFilter.trim()) params['categoryCode'] = this.categoryFilter.trim();
    if (this.normTypeFilter.trim()) params['normType'] = this.normTypeFilter.trim();
    if (this.search.trim()) params['search'] = this.search.trim();

    this.api.listByeLaws(params).subscribe({
      next: (res) => {
        this.rows.set(res.data || []);
        this.total.set(Number(res.meta?.total ?? res.data?.length ?? 0));
        this.page.set(Number(res.meta?.page ?? this.page()));
        this.totalPages.set(Number(res.meta?.totalPages ?? 1));
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Failed to load bye-laws');
      },
    });
  }

  rangeLabel(): string {
    if (!this.total()) return '0 of 0 norms shown';
    const start = (this.page() - 1) * this.pageSize() + 1;
    const end = Math.min(this.page() * this.pageSize(), this.total());
    return `${start}–${end} of ${this.total()} norms shown`;
  }

  goFirst(): void {
    if (this.page() === 1) return;
    this.page.set(1);
    this.reload();
  }

  goPrev(): void {
    if (this.page() <= 1) return;
    this.page.update((p) => p - 1);
    this.reload();
  }

  goNext(): void {
    if (this.page() >= this.totalPages()) return;
    this.page.update((p) => p + 1);
    this.reload();
  }

  goLast(): void {
    if (this.page() >= this.totalPages()) return;
    this.page.set(this.totalPages());
    this.reload();
  }



  openCreate(): void {

    this.editingId.set(null);

    this.form = this.emptyForm();

    this.modalOpen.set(true);

  }



  openEdit(r: ByeLawNorm, event?: Event): void {

    event?.stopPropagation();

    this.editingId.set(r.id);



    const category = this.categories().find((c) => c.code === r.categoryCode);

    let attributesJson = '';

    const attrs = (r as ByeLawNorm & { attributes?: unknown }).attributes;

    if (attrs != null) {

      try {

        attributesJson = typeof attrs === 'string' ? attrs : JSON.stringify(attrs, null, 2);

      } catch {

        attributesJson = String(attrs);

      }

    }



    this.form = {

      normTab: this.inferNormType(r),

      categoryId: category ? String(category.id) : '',

      useTypeId: String(r.useTypeId || ''),

      clauseRef: r.clauseRef || '3.2.1',

      effectiveFrom: this.toDateInput(r.effectiveFrom),

      effectiveTo: r.effectiveTo ? this.toDateInput(r.effectiveTo) : '',

      plotAreaMin: r.plotAreaMin != null ? String(r.plotAreaMin) : '',

      plotAreaMax: r.plotAreaMax != null ? String(r.plotAreaMax) : '',

      roadWidthMin: r.roadWidthMin != null ? String(r.roadWidthMin) : '',

      roadWidthMax: r.roadWidthMax != null ? String(r.roadWidthMax) : '',

      groundCoveragePct: r.groundCoveragePct != null ? String(r.groundCoveragePct) : '',

      farBase: r.farBase != null ? String(r.farBase) : '',

      farMax: r.farMax != null ? String(r.farMax) : '',

      heightMax: r.heightMax != null ? String(r.heightMax) : '',

      setbackFront: r.setbackFront != null ? String(r.setbackFront) : '',

      setbackRear: r.setbackRear != null ? String(r.setbackRear) : '',

      setbackSide1: r.setbackSide1 != null ? String(r.setbackSide1) : '',

      setbackSide2: r.setbackSide2 != null ? String(r.setbackSide2) : '',

      attributesJson,

      remarks: r.remarks || '',

    };

    this.modalOpen.set(true);

  }



  closeModal(): void {

    if (this.saving()) return;

    this.modalOpen.set(false);

  }



  setNormTab(tab: NormTab): void {

    this.form.normTab = tab;

    const match = NORM_TABS.find((t) => t.id === tab);

    if (match && !this.editingId()) {

      this.form.clauseRef = match.clause;

    }

  }



  onCategoryChange(): void {

    this.form.useTypeId = '';

  }



  inferNormType(r: ByeLawNorm): NormTab {

    if (r.clauseRef?.startsWith('3.2.5') || (r as ByeLawNorm & { attributes?: unknown }).attributes) {

      return 'ELECTRIC';

    }

    if (

      r.setbackFront != null ||

      r.setbackRear != null ||

      r.setbackSide1 != null ||

      r.setbackSide2 != null ||

      (r.clauseRef?.startsWith('3.2.4') ?? false)

    ) {

      return 'SETBACK';

    }

    if (r.heightMax != null || r.clauseRef?.startsWith('3.2.3')) return 'HEIGHT';

    if (

      r.farMax != null ||

      r.farBase != null ||

      r.groundCoveragePct != null ||

      r.clauseRef?.startsWith('3.2.2')

    ) {

      return 'FAR';

    }

    return 'PLOT_SIZE';

  }



  normTypeLabel(r: ByeLawNorm): string {

    const map: Record<NormTab, string> = {

      PLOT_SIZE: 'Plot size',

      FAR: 'FAR',

      HEIGHT: 'Height',

      SETBACK: 'Setback',

      ELECTRIC: 'Electric',

    };

    return map[this.inferNormType(r)];

  }



  normTypeClass(r: ByeLawNorm): string {

    const map: Record<NormTab, string> = {

      PLOT_SIZE: 'bl-norm-pill--plot',

      FAR: 'bl-norm-pill--far',

      HEIGHT: 'bl-norm-pill--height',

      SETBACK: 'bl-norm-pill--setback',

      ELECTRIC: 'bl-norm-pill--electric',

    };

    return map[this.inferNormType(r)];

  }



  formatPlotRange(r: ByeLawNorm): string {

    return this.formatRange(r.plotAreaMin, r.plotAreaMax);

  }



  formatRoadRange(r: ByeLawNorm): string {

    return this.formatRange(r.roadWidthMin, r.roadWidthMax);

  }



  formatRange(min: string | number | null | undefined, max: string | number | null | undefined): string {

    const lo = min != null && min !== '' ? Number(min) : null;

    const hi = max != null && max !== '' ? Number(max) : null;

    if (lo != null && hi != null) return `≥${this.trimNum(lo)} to <${this.trimNum(hi)}`;

    if (lo != null) return `≥${this.trimNum(lo)}`;

    if (hi != null) return `<${this.trimNum(hi)}`;

    return '—';

  }



  formatCell(v: string | number | null | undefined): string {

    if (v == null || v === '') return '—';

    return this.trimNum(Number(v));

  }



  formatHeight(r: ByeLawNorm): string {

    if (r.remarks?.toLowerCase().includes('no restriction')) return 'No restriction';

    if (r.heightMax == null || r.heightMax === '') return '—';

    return this.trimNum(Number(r.heightMax));

  }



  private trimNum(n: number): string {

    return Number.isInteger(n) ? String(n) : String(n).replace(/\.?0+$/, '');

  }



  private toDateInput(v: string | undefined): string {

    if (!v) return '2025-01-01';

    return v.slice(0, 10);

  }



  private numOrNull(v: string): number | null {

    return v.trim() ? Number(v) : null;

  }



  private parseAttributes(): unknown | null {

    const raw = this.form.attributesJson.trim();

    if (!raw) return null;

    return JSON.parse(raw);

  }



  save(): void {

    if (!this.form.useTypeId) {

      this.showToast('Select category and sub-type');

      return;

    }

    if (!this.form.clauseRef.trim()) {

      this.showToast('Clause reference is required');

      return;

    }



    let attributes: unknown | null = null;

    try {

      attributes = this.parseAttributes();

    } catch {

      this.showToast('Attributes must be valid JSON');

      return;

    }



    const body: Record<string, unknown> = {

      useTypeId: Number(this.form.useTypeId),

      clauseRef: this.form.clauseRef.trim(),

      effectiveFrom: this.form.effectiveFrom,

      effectiveTo: this.form.effectiveTo.trim() || null,

      plotAreaMin: this.numOrNull(this.form.plotAreaMin),

      plotAreaMax: this.numOrNull(this.form.plotAreaMax),

      roadWidthMin: this.numOrNull(this.form.roadWidthMin),

      roadWidthMax: this.numOrNull(this.form.roadWidthMax),

      groundCoveragePct: this.numOrNull(this.form.groundCoveragePct),

      farBase: this.numOrNull(this.form.farBase),

      farMax: this.numOrNull(this.form.farMax),

      heightMax: this.numOrNull(this.form.heightMax),

      setbackFront: this.numOrNull(this.form.setbackFront),

      setbackRear: this.numOrNull(this.form.setbackRear),

      setbackSide1: this.numOrNull(this.form.setbackSide1),

      setbackSide2: this.numOrNull(this.form.setbackSide2),

      attributes,

      remarks: this.form.remarks.trim() || null,

    };



    const id = this.editingId();

    this.saving.set(true);

    const req = id ? this.api.updateByeLaw(id, body) : this.api.createByeLaw(body);

    req.subscribe({

      next: () => {

        this.saving.set(false);

        this.showToast(id ? 'Norm updated' : 'Norm created');

        this.modalOpen.set(false);

        this.reload();

      },

      error: (err) => {

        this.saving.set(false);

        this.showToast(err?.error?.message || 'Save failed');

      },

    });

  }



  deactivate(r: ByeLawNorm, event: Event): void {

    event.stopPropagation();

    if (!confirm(`Delete norm for ${r.useTypeName || r.useTypeCode} (${r.clauseRef})?`)) return;

    this.api.deleteByeLaw(r.id).subscribe({

      next: () => {

        this.showToast('Norm deleted');

        this.reload();

      },

      error: (err) => this.showToast(err?.error?.message || 'Delete failed'),

    });

  }



  private showToast(msg: string): void {

    this.toast.set(msg);

    setTimeout(() => this.toast.set(''), 3200);

  }

}


